// web/src/components/RecCard.tsx
import { VerbBadge, type Verb } from "./VerbBadge";
import { RecActions, type ActionStatus } from "./RecActions";

export interface ReasonChip {
  code: string;
  text: string;
}

export interface RecCardProps {
  rec_id: string;
  verb: Verb;
  adset_name: string | null;
  spend: number;
  leads: number;
  cpl: number | null;
  daily_budget: number | null;
  reasons: ReasonChip[];
  suppressed_until: string | null;
  dismissed_at: string | null;
  action_status: ActionStatus | null;
  action_id: string | null;
  onAction: (input: { status: ActionStatus; snooze_days?: 7 | 14 }) => Promise<void>;
  onUnsnooze?: () => Promise<void>;
}

const BORDER: Record<Verb, string> = {
  scale: "#5fb37a", throttle: "#d9a64a", refresh_creative: "#5b9be8", kill: "#d96057",
};

const REASON_BG: Record<Verb, { bg: string; fg: string }> = {
  scale:            { bg: "#1f3a2a", fg: "#5fb37a" },
  throttle:         { bg: "#3a2f1f", fg: "#d9a64a" },
  refresh_creative: { bg: "#1c2c40", fg: "#5b9be8" },
  kill:             { bg: "#3a1f1f", fg: "#d96057" },
};

function fmtMoney(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

export function RecCard(props: RecCardProps) {
  const suppressed = props.suppressed_until !== null || props.dismissed_at !== null;
  const { bg, fg } = REASON_BG[props.verb];
  return (
    <div
      data-rec-id={props.rec_id}
      style={{
        background: "#1a1f2b", borderLeft: `3px solid ${BORDER[props.verb]}`,
        padding: "10px 12px", borderRadius: 4, marginBottom: 6,
        opacity: suppressed ? 0.5 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <VerbBadge verb={props.verb} />
        <strong style={{ fontSize: 13, color: "#cdd3dc" }}>
          {props.adset_name ?? "Unnamed ad set"}
        </strong>
      </div>
      <div style={{ fontSize: 11, color: "#7a808c", margin: "4px 0 6px" }}>
        {fmtMoney(props.spend)} spent / {props.leads} leads
        {props.cpl !== null && ` / ${fmtMoney(props.cpl)} CPL`}
        {props.daily_budget !== null && ` · cap ${fmtMoney(props.daily_budget)}/day`}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
        {props.reasons.map((r) => (
          <span key={r.code}
            style={{
              fontSize: 10, padding: "2px 7px", borderRadius: 10,
              background: bg, color: fg,
            }}
          >
            {r.text}
          </span>
        ))}
      </div>
      {suppressed && props.onUnsnooze ? (
        <div style={{ fontSize: 11, color: "#7a808c" }}>
          {props.action_status === "snoozed" && props.suppressed_until &&
            `Snoozed until ${new Date(props.suppressed_until).toLocaleDateString()}`}
          {props.action_status === "done"   && "Marked done"}
          {props.action_status === "dismissed" && "Dismissed"}
          {" · "}
          <button type="button" onClick={props.onUnsnooze}>Unsnooze</button>
        </div>
      ) : (
        <RecActions onAction={props.onAction} />
      )}
    </div>
  );
}
